import { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Command as CommandPrimitive } from 'cmdk'
import { Check, Plus, Search } from 'lucide-react'
import type { KeyboardEvent, ReactNode } from 'react'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { useIsTouchPicker } from '@/hooks/useMobile'
import { cn } from '@/lib/utils'

export interface PickerOption {
  value: string
  /** Plain text: rendered as the row name and matched by search. */
  label: string
  /** Leading glyph (status shape, avatar, priority bars, label dot). */
  icon?: ReactNode
  /** Right-hand column: open load, usage count, a date. Falls back to `shortcut`. */
  meta?: ReactNode
  keywords?: Array<string>
  /** Group heading. Ungrouped options render first, without a heading. */
  group?: string
  /** A single key that sets this option while the search box is empty. */
  shortcut?: string
}

export interface PickerAction {
  label: string
  icon?: ReactNode
  onSelect: () => void
}

export interface PickerHint {
  keys: string
  label: string
}

export interface PickerPanelProps {
  options: Array<PickerOption>
  selected?: string | Array<string> | null
  onSelect: (value: string) => void
  /** Checkboxes instead of a tick; Space toggles. The caller keeps the popover open. */
  multiple?: boolean
  searchPlaceholder: string
  /** Show group headings. Defaults to "more than 6 options". */
  groupHeadings?: boolean
  /** Footer key hints. Defaults to the list keys for the mode; `false` hides the footer. */
  hints?: Array<PickerHint> | false
  /** A trailing row under a rule, e.g. "Edit workflow…". Hidden while searching. */
  action?: PickerAction
  /** Offer `+ Create "query"` when the query matches no option by name. */
  onCreate?: (query: string) => void
  /** Content above the list, e.g. the picked labels as chips. */
  header?: ReactNode
  emptyText?: string
  className?: string
}

const CREATE_VALUE = '__picker_create__'
const ACTION_VALUE = '__picker_action__'

const matchesSearch = (_value: string, search: string, keywords?: Array<string>) => {
  const haystack = (keywords ?? []).join(' ').toLowerCase()
  const terms = search.toLowerCase().split(/\s+/).filter(Boolean)
  return terms.every((term) => haystack.includes(term)) ? 1 : 0
}

/** Popover surface for a PickerPanel: 264px card, soft shadow, hairline ring. */
export const pickerPopoverClassName =
  'w-[264px] overflow-hidden rounded-xl border-0 p-0 shadow-[0_16px_36px_-12px_rgba(31,29,26,.3)] ring-1 ring-foreground/[.07]'

/** Gap between the trigger chip and the panel. */
export const PICKER_SIDE_OFFSET = 6

/** Ink ring on the chip whose panel is open. Applied before the caller's classes, so
 * a trigger can opt out with e.g. `data-[state=open]:shadow-none`. */
export const pickerTriggerOpenClassName =
  'data-[state=open]:border-foreground data-[state=open]:shadow-[0_0_0_3px_var(--muted)]'

/**
 * The one combobox shell for every property (design 31b): search first, groups when
 * there are more than six options, the current value ticked (or checkboxes for
 * multi-select), faint shortcut digits on the right and the in-list keys in a footer.
 */
export function PickerPanel({
  options,
  selected,
  onSelect,
  multiple = false,
  searchPlaceholder,
  groupHeadings,
  hints,
  action,
  onCreate,
  header,
  emptyText,
  className,
}: PickerPanelProps) {
  const { t } = useTranslation('common')
  const isMobile = useIsTouchPicker()
  const [query, setQuery] = useState('')
  // Opening the sheet must not focus search. A phone keyboard then resizes
  // the page and the sheet opens and closes in a loop (MOL-41).
  const allowSearchFocus = useRef(false)
  const [active, setActive] = useState('')

  const selectedSet = useMemo(
    () =>
      new Set(
        Array.isArray(selected) ? selected : selected != null ? [selected] : [],
      ),
    [selected],
  )

  const showHeadings = groupHeadings ?? options.length > 6

  const groups = useMemo(() => {
    const ordered = new Map<string, Array<PickerOption>>()
    const ungrouped: Array<PickerOption> = []
    for (const option of options) {
      if (!option.group) {
        ungrouped.push(option)
        continue
      }
      const list = ordered.get(option.group) ?? []
      list.push(option)
      ordered.set(option.group, list)
    }
    return [
      { heading: undefined, options: ungrouped },
      ...Array.from(ordered, ([heading, list]) => ({ heading, options: list })),
    ].filter((group) => group.options.length > 0)
  }, [options])

  const shortcutKeys = useMemo(
    () => options.map((o) => o.shortcut).filter((key): key is string => !!key),
    [options],
  )

  const trimmedQuery = query.trim()
  const canCreate =
    !!onCreate &&
    trimmedQuery.length > 0 &&
    !options.some((o) => o.label.toLowerCase() === trimmedQuery.toLowerCase())

  const footerHints = useMemo<Array<PickerHint>>(() => {
    if (hints === false) return []
    if (hints) return hints
    if (multiple) {
      return [
        { keys: '↑↓', label: t('picker.keys.move') },
        { keys: t('picker.keys.space'), label: t('picker.keys.toggle') },
        { keys: 'esc', label: t('picker.keys.done') },
      ]
    }
    const base = [
      { keys: '↑↓', label: t('picker.keys.move') },
      { keys: '↵', label: t('picker.keys.set') },
    ]
    const first = shortcutKeys[0]
    const last = shortcutKeys[shortcutKeys.length - 1]
    if (first && last) {
      base.push({
        keys: first === last ? first : `${first}–${last}`,
        label: t('picker.keys.jump'),
      })
    }
    return base
  }, [hints, multiple, shortcutKeys, t])

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.metaKey || event.ctrlKey || event.altKey || query !== '') return

    if (multiple && event.key === ' ' && active && active !== ACTION_VALUE) {
      event.preventDefault()
      onSelect(active)
      return
    }

    const match = options.find((o) => o.shortcut === event.key)
    if (match) {
      event.preventDefault()
      onSelect(match.value)
    }
  }

  // Phones (design 36b): 48px full-width rows; the current value is tinted,
  // not the keyboard cursor, which a touch user never moves.
  const rowClass = isMobile
    ? 'grid h-12 grid-cols-[18px_minmax(0,1fr)_auto_16px] items-center gap-3 px-4 py-0 text-[15px] data-[selected=true]:bg-transparent data-[checked=true]:bg-brand-tint/60'
    : 'mx-1 grid h-8 grid-cols-[16px_minmax(0,1fr)_auto_14px] items-center gap-[9px] rounded-[7px] px-2.5 py-0 text-[13px] data-[selected=true]:bg-muted data-[selected=true]:text-foreground'

  return (
    <Command
      filter={matchesSearch}
      value={active}
      onValueChange={setActive}
      onKeyDown={handleKeyDown}
      className={cn('rounded-none bg-popover', className)}
    >
      <div className="flex h-10 items-center gap-2 border-b border-border/70 px-3 phone:h-12 phone:px-4">
        <Search className="size-3.5 shrink-0 text-muted-foreground phone:size-4" />
        <CommandPrimitive.Input
          value={query}
          onValueChange={setQuery}
          placeholder={searchPlaceholder}
          className="h-full min-w-0 flex-1 bg-transparent text-[13px] outline-none placeholder:text-muted-foreground phone:text-base"
          onPointerDown={() => {
            allowSearchFocus.current = true
          }}
          onFocus={(event) => {
            if (isMobile && !allowSearchFocus.current) event.currentTarget.blur()
          }}
        />
      </div>

      {header}

      <CommandList className="max-h-[400px] pb-1.5 pt-0.5">
        {!canCreate && (
          <CommandEmpty className="py-5 text-center text-[13px] text-muted-foreground">
            {emptyText ?? t('states.noResults')}
          </CommandEmpty>
        )}

        {groups.map((group) => (
          <CommandGroup
            key={group.heading ?? '__ungrouped__'}
            heading={showHeadings ? group.heading : undefined}
            className="p-0 [&_[cmdk-group-heading]]:px-2.5 [&_[cmdk-group-heading]]:pb-[3px] [&_[cmdk-group-heading]]:pt-2 [&_[cmdk-group-heading]]:text-[10.5px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-[.08em]"
          >
            {group.options.map((option) => {
              const isSelected = selectedSet.has(option.value)
              // Number-key shortcuts mean nothing without a keyboard.
              const meta = option.meta ?? (isMobile ? undefined : option.shortcut)
              return (
                <CommandItem
                  key={option.value}
                  value={option.value}
                  keywords={[option.label, ...(option.keywords ?? [])]}
                  onSelect={() => onSelect(option.value)}
                  data-checked={isSelected || undefined}
                  className={rowClass}
                >
                  <span className="grid place-items-center">
                    {multiple ? <PickerCheckbox checked={isSelected} /> : option.icon}
                  </span>
                  <span className="flex min-w-0 items-center gap-1.5">
                    {multiple ? option.icon : null}
                    <span className="truncate">{option.label}</span>
                  </span>
                  <span className="font-mono text-[10.5px] text-muted-foreground">
                    {meta}
                  </span>
                  <span className="grid place-items-center">
                    {!multiple && isSelected ? (
                      <Check className="size-[13px] text-foreground phone:size-4 phone:text-brand" />
                    ) : null}
                  </span>
                </CommandItem>
              )
            })}
          </CommandGroup>
        ))}

        {canCreate && (
          <CommandGroup forceMount className="p-0">
            <CommandItem
              forceMount
              value={CREATE_VALUE}
              onSelect={() => {
                onCreate(trimmedQuery)
                setQuery('')
              }}
              className={rowClass}
            >
              <span className="grid place-items-center">
                <Plus className="size-3.5 text-muted-foreground" />
              </span>
              <span className="truncate">
                {t('picker.create', { name: trimmedQuery })}
              </span>
              <span />
              <span />
            </CommandItem>
          </CommandGroup>
        )}

        {action && query === '' && (
          <CommandGroup className="mt-1 border-t border-border/70 p-0 pt-1">
            <CommandItem
              value={ACTION_VALUE}
              onSelect={action.onSelect}
              className="mx-1 flex h-[30px] items-center gap-2 rounded-[7px] px-2.5 py-0 text-[12.5px] text-muted-foreground data-[selected=true]:bg-muted phone:mx-0 phone:h-12 phone:rounded-none phone:px-4 phone:text-sm"
            >
              {action.icon}
              {action.label}
            </CommandItem>
          </CommandGroup>
        )}
      </CommandList>

      {!isMobile && footerHints.length > 0 && (
        <div className="flex h-8 items-center gap-2.5 border-t border-border/70 bg-muted/40 px-3 text-[11px] text-muted-foreground">
          {footerHints.map((hint) => (
            <span key={hint.label} className="flex items-center gap-1">
              <kbd className="rounded border border-b-2 border-border bg-background px-[5px] font-mono text-[10.5px] leading-4 text-muted-foreground">
                {hint.keys}
              </kbd>
              {hint.label}
            </span>
          ))}
        </div>
      )}
    </Command>
  )
}

function PickerCheckbox({ checked }: { checked: boolean }) {
  return checked ? (
    <span className="grid size-3.5 place-items-center rounded-[4px] bg-foreground text-background">
      <Check className="size-[9px]" strokeWidth={3} />
    </span>
  ) : (
    <span className="size-3.5 rounded-[4px] border-[1.5px] border-muted-foreground/40" />
  )
}
